import React, { useEffect, useMemo, useState } from 'react';
import { ContextualAiBubble } from '@/features/super-ai/components/ContextualAiBubble';
import { SuperAiWorkspaceModal } from '@/features/super-ai/components/SuperAiWorkspaceModal';
import type {
  AcademicContact,
  AttachmentItem,
  CommunicationsOverview,
  ConversationDetail,
  LinkedObject,
} from '../types';
import {
  createGroupConversation,
  getAcademicContacts,
  getCommunicationsOverview,
  getConversationDetail,
  markConversationAsRead,
  resolveDirectConversation,
  sendMessage,
  toggleConversationPin,
} from '../api';

const PERSONA_OPTIONS = [
  ['SYSTEM_OWNER', 'Owner'],
  ['ADVISOR', 'Advisor'],
  ['SUPERVISOR', 'Supervisor'],
  ['TEACHER', 'Teacher'],
  ['RESEARCHER', 'Researcher'],
] as const;

const LINKED_OBJECT_LABEL: Record<string, string> = {
  PROJECT: 'Проект',
  ORDER: 'Заказ',
  ARTICLE: 'Статья',
  INTAKE: 'Обращение',
  REQUEST: 'Заявка',
};

interface AcademicConnectionsViewProps {
  roleContext?: string;
  roleTitle?: string;
  userRoleCodes?: string[];
  onBack?: () => void;
  onClose?: () => void;
  onOpenLinkedObject?: (linkedObject: LinkedObject) => void;
  embedded?: boolean;
  initialTarget?: {
    userId?: string;
    academiaId?: string;
    role?: string;
    linkedObject?: LinkedObject;
  };
}

const SUB_FILTERS = [
  ['ALL', 'Все диалоги'],
  ['UNREAD', 'Непрочитанные'],
  ['PINNED', 'Закреплённые'],
  ['PROJECT', 'Проектные'],
  ['EDUCATION', 'Учебные'],
  ['ADMIN', 'Административные'],
] as const;

const CONTEXT_LABEL: Record<string, string> = {
  PROJECT: 'Проектный',
  EDUCATION: 'Учебный',
  ADMIN: 'Административный',
};

const CONTEXT_TAG: Record<string, string> = {
  PROJECT: 'Проект',
  EDUCATION: 'Обучение',
  ADMIN: 'Администрирование',
};

function formatMessageTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ru-RU');
  } catch {
    return iso;
  }
}

function deliveryTicks(state: string): { cls: string; mark: string } {
  if (state === 'READ') return { cls: 'read', mark: '✓✓' };
  if (state === 'DELIVERED') return { cls: 'delivered', mark: '✓✓' };
  return { cls: '', mark: '✓' };
}

/** Academic connections — template U142 messenger (acad-conn-*). */
export const AcademicConnectionsView: React.FC<AcademicConnectionsViewProps> = ({
  roleContext = 'SYSTEM_OWNER',
  roleTitle = 'Владелец системы',
  userRoleCodes,
  onBack,
  onClose,
  onOpenLinkedObject,
  embedded = false,
  initialTarget,
}) => {
  const [activeRoleContext, setActiveRoleContext] = useState(roleContext);
  const [overview, setOverview] = useState<CommunicationsOverview | null>(null);
  const [contacts, setContacts] = useState<AcademicContact[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('Все');
  const [subFilter, setSubFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [activeConversation, setActiveConversation] = useState<ConversationDetail | null>(null);
  const [messageText, setMessageText] = useState<string>('');
  const [selectedAttachment, setSelectedAttachment] = useState<AttachmentItem | null>(null);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [copiedId, setCopiedId] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [superAiOpen, setSuperAiOpen] = useState(false);
  const [superAiPrompt, setSuperAiPrompt] = useState('');
  const [superAiContextMode, setSuperAiContextMode] = useState('auto');

  useEffect(() => {
    setActiveRoleContext(roleContext);
  }, [roleContext]);

  const showPersonaSelector =
    roleContext === 'SYSTEM_OWNER' || (userRoleCodes?.length ?? 0) > 1;

  const loadData = async () => {
    try {
      setLoading(true);
      const [ovData, contactsData] = await Promise.all([
        getCommunicationsOverview(activeRoleContext),
        getAcademicContacts({
          role_context: activeRoleContext,
          category: activeCategory !== 'Все' ? activeCategory : undefined,
          sub_filter: subFilter,
          search: searchQuery.trim() || undefined,
        }),
      ]);
      setOverview(ovData);
      setContacts(contactsData);

      if (initialTarget && (initialTarget.userId || initialTarget.academiaId)) {
        const resolved = await resolveDirectConversation(
          {
            target_user_id: initialTarget.userId,
            target_academia_id: initialTarget.academiaId,
            target_role: initialTarget.role,
            linked_object_type: initialTarget.linkedObject?.type,
            linked_object_id: initialTarget.linkedObject?.id,
            linked_object_title: initialTarget.linkedObject?.title,
          },
          activeRoleContext,
        );
        setSelectedConversationId(resolved.id);
        setActiveConversation(resolved);
      } else if (contactsData.length > 0) {
        setSelectedConversationId((prev) => {
          if (prev && contactsData.some((c) => c.conversation_id === prev)) return prev;
          return contactsData[0].conversation_id;
        });
      } else {
        setSelectedConversationId(null);
        setActiveConversation(null);
      }
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [activeRoleContext, activeCategory, subFilter]);

  useEffect(() => {
    if (!selectedConversationId) return;
    markConversationAsRead(selectedConversationId).catch(() => {});
    getConversationDetail(selectedConversationId)
      .then((detail) => {
        setActiveConversation(detail);
        setContacts((prev) =>
          prev.map((c) =>
            c.conversation_id === selectedConversationId ? { ...c, unread_count: 0 } : c,
          ),
        );
      })
      .catch(() => {});
  }, [selectedConversationId]);

  const runSearch = () => {
    getAcademicContacts({
      role_context: activeRoleContext,
      category: activeCategory !== 'Все' ? activeCategory : undefined,
      sub_filter: subFilter,
      search: searchQuery.trim() || undefined,
    }).then(setContacts);
  };

  const handleCreateGroup = async () => {
    const title = window.prompt('Название группы');
    if (!title?.trim()) return;
    const idsRaw = window.prompt(
      'ID участников через запятую (user_id из контактов)',
      contacts
        .filter((c) => c.conversation_type !== 'GROUP')
        .slice(0, 2)
        .map((c) => c.user_id)
        .join(','),
    );
    if (!idsRaw?.trim()) return;
    try {
      const participant_user_ids = idsRaw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      const created = await createGroupConversation({
        title: title.trim(),
        participant_user_ids,
        role_context: activeRoleContext,
      });
      setSelectedConversationId(created.id);
      setActiveConversation(created);
      await loadData();
    } catch {
      /* ignore */
    }
  };

  const handleSelectContact = async (contact: AcademicContact) => {
    if (contact.conversation_type === 'GROUP' || contact.has_conversation !== false) {
      setSelectedConversationId(contact.conversation_id);
      return;
    }
    try {
      const resolved = await resolveDirectConversation(
        {
          target_user_id: contact.user_id,
          target_role: contact.role_context,
        },
        activeRoleContext,
      );
      setSelectedConversationId(resolved.id);
      setActiveConversation(resolved);
      await loadData();
    } catch {
      /* ignore */
    }
  };

  const handleTogglePin = async () => {
    if (!selectedConversationId) return;
    try {
      const res = await toggleConversationPin(selectedConversationId);
      setActiveConversation((prev) => (prev ? { ...prev, is_pinned: res.is_pinned } : null));
      setContacts((prev) =>
        prev.map((c) =>
          c.conversation_id === selectedConversationId ? { ...c, is_pinned: res.is_pinned } : c,
        ),
      );
    } catch {
      /* ignore */
    }
  };

  const handleCopyId = (academiaId: string) => {
    navigator.clipboard.writeText(academiaId);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if ((!messageText.trim() && !selectedAttachment) || isSending || !selectedConversationId) {
      return;
    }
    try {
      setIsSending(true);
      const attachmentsList = selectedAttachment ? [selectedAttachment] : [];
      const newMsg = await sendMessage(selectedConversationId, {
        body: messageText.trim() || 'Вложенный файл',
        attachments: attachmentsList,
        sender_role: activeRoleContext,
      });
      setActiveConversation((prev) =>
        prev
          ? {
              ...prev,
              messages: [...prev.messages, newMsg],
              last_message_at: newMsg.created_at,
            }
          : null,
      );
      setMessageText('');
      setSelectedAttachment(null);
      setContacts((prev) =>
        prev.map((c) =>
          c.conversation_id === selectedConversationId
            ? {
                ...c,
                last_message_preview: newMsg.body,
                last_message_time: 'Только что',
              }
            : c,
        ),
      );
    } catch {
      /* ignore */
    } finally {
      setIsSending(false);
    }
  };

  const categoryFilters = useMemo(() => {
    const cats = new Set<string>();
    contacts.forEach((c) => {
      if (c.relation_label) cats.add(c.relation_label);
      else if (c.category) cats.add(c.category);
    });
    return ['Все', ...Array.from(cats)];
  }, [contacts]);

  const activeLabel =
    activeCategory === 'Все' ? 'Все категории' : activeCategory;

  const peer = activeConversation?.contact
    ?? contacts.find((c) => c.conversation_id === selectedConversationId)
    ?? null;

  const linkedObject =
    activeConversation?.linked_object
    ?? peer?.linked_object
    ?? initialTarget?.linkedObject
    ?? null;

  const contextValue = peer
    ? CONTEXT_LABEL[peer.context] || peer.context || 'Общий'
    : '—';

  const contextualIncomingText =
    activeConversation?.messages.filter((m) => !m.is_me).slice(-1)[0]?.body ||
    peer?.last_message_preview ||
    messageText ||
    '';

  const body = (
    <section className="acad-conn-academic">
      <div className="acad-conn-kpis">
        <article className="acad-conn-kpi" style={{ ['--a' as string]: '#41d2ff' }}>
          <span className="acad-conn-kpi-icon" aria-hidden="true">
            ◫
          </span>
          <div>
            <small>Диалоги</small>
            <strong>{overview ? overview.dialogs_count : contacts.length}</strong>
            <p>единая база контактов</p>
          </div>
        </article>
        <article className="acad-conn-kpi" style={{ ['--a' as string]: '#e2ad3a' }}>
          <span className="acad-conn-kpi-icon" aria-hidden="true">
            ●
          </span>
          <div>
            <small>Непрочитанные</small>
            <strong>{overview ? overview.unread_count : 0}</strong>
            <p>сообщения, требующие внимания</p>
          </div>
        </article>
        <article className="acad-conn-kpi" style={{ ['--a' as string]: '#9e80ff' }}>
          <span className="acad-conn-kpi-icon" aria-hidden="true">
            ★
          </span>
          <div>
            <small>Закреплённые</small>
            <strong>
              {overview ? overview.pinned_count : contacts.filter((c) => c.is_pinned).length}
            </strong>
            <p>быстрый доступ к важным людям</p>
          </div>
        </article>
        <article className="acad-conn-kpi" style={{ ['--a' as string]: '#49ddb7' }}>
          <span className="acad-conn-kpi-icon" aria-hidden="true">
            ⌘
          </span>
          <div>
            <small>Категории</small>
            <strong>
              {overview ? overview.categories_count : Math.max(0, categoryFilters.length - 1)}
            </strong>
            <p>доступные категории и текущие диалоги</p>
          </div>
        </article>
      </div>

      <nav className="acad-conn-category-strip" aria-label="Категории академических связей">
        {categoryFilters.map((label) => (
          <button
            key={label}
            type="button"
            className={activeCategory === label ? 'active' : ''}
            onClick={() => setActiveCategory(label)}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="acad-conn-toolbar">
        {showPersonaSelector ? (
          <label className="acad-conn-persona">
            <span>Пишете как:</span>
            <select
              value={activeRoleContext}
              onChange={(e) => setActiveRoleContext(e.target.value)}
              aria-label="Ролевой контекст переписки"
            >
              {PERSONA_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="acad-conn-search">
          <span aria-hidden="true">⌕</span>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                runSearch();
              }
            }}
            onBlur={runSearch}
            placeholder="Поиск по фамилии, имени или ID"
            aria-label="Поиск контактов"
          />
        </label>
        <div className="acad-conn-subfilters" role="group" aria-label="Фильтр диалогов">
          {SUB_FILTERS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={subFilter === id ? 'active' : ''}
              onClick={() => setSubFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="acad-conn-messenger">
        <aside className="acad-conn-list">
          <header className="acad-conn-list-head">
            <div>
              <small>ДИАЛОГИ</small>
              <h3>{contacts.length} контактов</h3>
            </div>
            <div className="acad-conn-list-head-actions">
              <button type="button" className="acad-conn-group-create" onClick={() => void handleCreateGroup()}>
                Группа
              </button>
              <span>{activeLabel}</span>
            </div>
          </header>
          <div className="acad-conn-list-scroll">
            {loading && contacts.length === 0 ? (
              <div className="acad-conn-empty">Загрузка контактов…</div>
            ) : null}
            {!loading && contacts.length === 0 ? (
              <div className="acad-conn-empty">По выбранным фильтрам контактов нет.</div>
            ) : null}
            {contacts.map((contact) => {
              const tag = CONTEXT_TAG[contact.context] || 'Связь';
              const isGroup = contact.conversation_type === 'GROUP';
              return (
                <button
                  key={contact.conversation_id}
                  type="button"
                  className={`acad-conn-contact${
                    contact.conversation_id === selectedConversationId ? ' active' : ''
                  }`}
                  onClick={() => void handleSelectContact(contact)}
                >
                  <span className="acad-conn-avatar">{contact.initials}</span>
                  <span className="acad-conn-contact-copy">
                    <strong>{contact.full_name}</strong>
                    <small>
                      {isGroup
                        ? 'Группа'
                        : `${contact.relation_label || contact.category || 'Контакт'} · ${contact.academia_id}`}
                    </small>
                    <em>{contact.last_message_preview || 'Открыть диалог'}</em>
                    <span className="acad-conn-contact-meta">
                      {isGroup ? <i className="acad-conn-tag group">Группа</i> : null}
                      <i className="acad-conn-tag">{tag}</i>
                      {contact.is_pinned ? (
                        <i className="acad-conn-tag pin">★ Закреплён</i>
                      ) : null}
                    </span>
                  </span>
                  <span className="acad-conn-contact-side">
                    <time>{contact.last_message_time || '—'}</time>
                    {contact.unread_count > 0 ? (
                      <b className="acad-conn-unread">{contact.unread_count}</b>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="acad-conn-chat">
          {peer ? (
            <>
              <header className="acad-conn-chat-head">
                <span className="acad-conn-avatar large">{peer.initials}</span>
                <div className="acad-conn-chat-person">
                  <small>АКАДЕМИЧЕСКИЙ КОНТАКТ</small>
                  <h3>{peer.full_name}</h3>
                  <p>
                    {activeConversation?.type === 'GROUP'
                      ? `Групповой диалог · ${activeConversation.messages.length} сообщений`
                      : `${peer.relation_label || peer.category || 'Контакт'} · ${peer.academia_id} · зарегистрированный пользователь`}
                  </p>
                </div>
                <div className="acad-conn-chat-actions">
                  {linkedObject ? (
                    <button
                      type="button"
                      className="acad-conn-linked-chip"
                      onClick={() => onOpenLinkedObject?.(linkedObject)}
                      title={linkedObject.title || linkedObject.id}
                    >
                      {LINKED_OBJECT_LABEL[linkedObject.type] || linkedObject.type} ·{' '}
                      {linkedObject.title || linkedObject.id}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={
                      activeConversation?.is_pinned || peer.is_pinned ? 'active' : ''
                    }
                    onClick={handleTogglePin}
                  >
                    {activeConversation?.is_pinned || peer.is_pinned
                      ? '★ Закреплено'
                      : '☆ Закрепить'}
                  </button>
                  <button type="button" onClick={() => handleCopyId(peer.academia_id)}>
                    {copiedId ? 'Скопировано' : 'Копировать ID'}
                  </button>
                </div>
              </header>

              <div className="acad-conn-context">
                <article>
                  <span>Категория</span>
                  <strong>{peer.relation_label || peer.category || '—'}</strong>
                </article>
                <article>
                  <span>ID</span>
                  <strong>{peer.academia_id}</strong>
                </article>
                <article>
                  <span>Контекст</span>
                  <strong>{contextValue}</strong>
                </article>
                <article>
                  <span>Статус</span>
                  <strong>
                    {peer.online_status === 'online' ? '● Онлайн' : '○ Не в сети'}
                  </strong>
                </article>
              </div>

              <div className="acad-conn-messages" id="acadConnMessages">
                {!activeConversation || activeConversation.messages.length === 0 ? (
                  <div className="acad-conn-empty">
                    Диалог ещё не начат. Напишите первое сообщение.
                  </div>
                ) : (
                  activeConversation.messages.map((m) => {
                    const ticks = deliveryTicks(m.delivery_state);
                    return (
                      <article
                        key={m.id}
                        className={`acad-conn-msg${m.is_me ? ' mine' : ''}`}
                      >
                        <p>{m.body}</p>
                        <footer>
                          <time>{formatMessageTime(m.created_at)}</time>
                          {m.is_me ? (
                            <span className={`acad-conn-ticks ${ticks.cls}`}>{ticks.mark}</span>
                          ) : null}
                        </footer>
                      </article>
                    );
                  })
                )}
              </div>

              <footer className="acad-conn-compose">
                <div className="acad-conn-compose-tools">
                  <ContextualAiBubble
                    incomingText={contextualIncomingText}
                    threadId={selectedConversationId || undefined}
                    onInsert={(text) => setMessageText(text)}
                    onOpenWorkspace={(initialPrompt, contextMode) => {
                      setSuperAiPrompt(initialPrompt);
                      setSuperAiContextMode(contextMode);
                      setSuperAiOpen(true);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setMessageText(
                        (prev) =>
                          prev ||
                          'Здравствуйте! Кратко опишите вопрос или статус по текущей работе.',
                      )
                    }
                  >
                    Шаблон ответа
                  </button>
                  <span>Сообщение хранится в общей записи диалога и будет видно второй стороне.</span>
                </div>
                {selectedAttachment ? (
                  <div className="acad-conn-empty" style={{ textAlign: 'left', padding: '0 0 8px' }}>
                    {selectedAttachment.name} ({selectedAttachment.size_label}){' '}
                    <button type="button" onClick={() => setSelectedAttachment(null)}>
                      Убрать
                    </button>
                  </div>
                ) : null}
                <form className="acad-conn-compose-row" onSubmit={handleSendMessage}>
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Напишите сообщение…"
                  />
                  <button type="submit" className="acad-conn-send" disabled={isSending}>
                    Отправить
                  </button>
                </form>
              </footer>
            </>
          ) : (
            <div className="acad-conn-empty">Выберите контакт в списке диалогов.</div>
          )}
        </section>
      </div>
    </section>
  );

  if (embedded) {
    return (
      <>
        {body}
        <SuperAiWorkspaceModal
          isOpen={superAiOpen}
          onClose={() => setSuperAiOpen(false)}
          activeRole={activeRoleContext}
          initialPrompt={superAiPrompt}
          initialContextMode={superAiContextMode}
        />
      </>
    );
  }

  return (
    <div className="acad-conn-overlay" role="dialog" aria-modal="true" aria-label="Академические связи">
      <div className="acad-conn-route">
        <header className="acad-conn-top">
          {onBack ? (
            <button type="button" onClick={onBack}>
              ← Назад
            </button>
          ) : null}
          <div>
            <h1>Академические связи</h1>
            <p>{roleTitle}</p>
          </div>
          <span className="acad-conn-spacer" />
          {onClose ? (
            <button type="button" onClick={onClose}>
              Закрыть ×
            </button>
          ) : null}
        </header>
        <main className="acad-conn-content">{body}</main>
      </div>
      <SuperAiWorkspaceModal
        isOpen={superAiOpen}
        onClose={() => setSuperAiOpen(false)}
        activeRole={activeRoleContext}
        initialPrompt={superAiPrompt}
        initialContextMode={superAiContextMode}
      />
    </div>
  );
};
